import { useMemo } from 'react'
import { FieldErrors, useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useRecoilState } from 'recoil'
import { useNavigate } from 'react-router-dom'
import { getAnalytics, setUserId, setUserProperties } from 'firebase/analytics'

import vendorAtom from '@src/atom/vendor'
import useSignupQuery from '@src/hooks/queries/auth/signup/useSignupQuery'
import useVendorInfoMutate from '@src/hooks/queries/auth/signup/useVendorInfoMutate'
import useVendorContactsMutate from '@src/hooks/queries/auth/signup/useVendorContactsMutate'

import { SignupFormType } from '@src/components/signup/types'
import { CreateVendorInfoPayload } from '@src/lib/api/vendors/createVendorInfo'
import { CreateVendorContactsPayload } from '@src/lib/api/vendors/createVendorContacts'

const useSignup = () => {
	const signupSchema = useMemo(
		() =>
			Yup.object({
				username: Yup.string().required('아이디를 입력해주세요.'),
				password: Yup.string()
					.required('비밀번호를 입력해주세요.')
					.matches(
						/^(?=.*[a-z])(?=.*\d)(?=.*[`!@#$%^&*×÷€£¥•()_+\-=[\]{};':"\\|,.<>\\/?~])[A-Za-z\d`!@#$%^&*×÷€£¥•()_+\-=\\[\]{};':"\\|,.<>\\/?~]{8,}/,
						'영문, 숫자, 특수문자 포함 8자 이상으로 설정해 주세요.',
					),
				passwordConfirm: Yup.string()
					.required('비밀번호를 입력해주세요.')
					.oneOf([Yup.ref('password'), null], '비밀번호가 일치하지 않습니다.'),
				brandLogo: Yup.mixed().required('브랜드 로고를 업로드해주세요.'),
				phone: Yup.string()
					.required('대표자 휴대폰 번호를 입력해주세요.')
					.min(8, '연락처는 8자 이상이여야 합니다.')
					.matches(
						/^(0[2-8][0-5]?|01[01346-9]?|(1544|1566|1577|1588|1644|1688))-?(([1-9]{1}[0-9]{2,3})-?([0-9]{4}))?|([0-9]{4})$/,
						'올바른 지역번호 또는 대표번호를 입력해주세요.',
					),
				name: Yup.string().required('브랜드명을 입력해주세요'),
				businessRegistration: Yup.mixed().required('사업자등록증을 업로드해주세요.'),
				businessRegistrationNumber: Yup.string()
					.required('사업자등록번호를 입력해주세요.')
					.matches(/([0-9]{3})-?([0-9]{2})-?([0-9]{5})/, '올바른 사업자 등록번호를 입력하세요.')
					.transform((value: string) => {
						const businessRegistrationNumber = value.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3')
						return businessRegistrationNumber
					}),
				salesReport: Yup.mixed().required('통신판매업증을 업로드헤주세요.'),
				salesReportNumber: Yup.string().required('통신판매업 신고번호를 입력해주세요.'),
				ceoName: Yup.string().required('대표자명을 입력해주세요.'),
				companyName: Yup.string().required('업체(법인)명을 입력해주세요.'),
				email: Yup.string()
					.required('이메일을 입력해주세요.')
					.matches(
						/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i,
						'이메일 형식이 올바르지 않습니다.',
					),
				vendorStoreLink: Yup.string()
					.required('사이트 URL을 입력해주세요.')
					.transform((link: string) => {
						const isValidStoreLink = /^((https|http):\/\/)/.test(link)
						if (!isValidStoreLink) {
							return `https://${link}`
						}
						return link
					})
					.url('유효하지 않은 URL입니다.'),
				companyDescription: Yup.string().required('브랜드 소개를 작성해주세요.'),
				address: Yup.string().required('사업장 소지를 입력해주세요.'),
				vendorContacts: Yup.array().of(
					Yup.object({
						name: Yup.string().required('담당자명을 입력해주세요.'),
						position: Yup.string().required('담당자 직책을 입력해주세요.'),
						email: Yup.string()
							.required('담당자 이메일을 입력해주세요.')
							.matches(
								/^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i,
								'이메일 형식이 올바르지 않습니다.',
							),
						phone: Yup.string()
							.required('담당자 연락처를 입력해주세요.')
							.min(9, '연락처는 9자 이상이여야 합니다.')
							.matches(
								/^(0[2-8][0-5]?|01[01346-9])-?([1-9]{1}[0-9]{2,3})-?([0-9]{4})$/,
								'올바른 지역번호 또는 대표번호를 입력해주세요.',
							),
					}),
				),
			}).required(),
		[],
	)

	const methods = useForm<SignupFormType>({
		defaultValues: {
			username: '',
			password: '',
			passwordConfirm: '',
			name: '',
			vendorStoreLink: '',
			businessRegistrationNumber: '',
			salesReportNumber: '',
			companyName: '',
			ceoName: '',
			address: '',
			email: '',
			phone: '',
			vendorContacts: [{ name: '', position: '', email: '', phone: '' }],
		},
		resolver: yupResolver(signupSchema),
		mode: 'onTouched',
	})

	const [, setVendor] = useRecoilState(vendorAtom)
	const { mutateAsync: signupMutateAsync, isLoading } = useSignupQuery()
	const { mutateAsync: createVendorInfoMutateAsync } = useVendorInfoMutate()
	const { mutateAsync: createVendorContactsMutateAsync } = useVendorContactsMutate()

	const navigate = useNavigate()

	// TODO : CreateVendorInfo Fetch &  CreatVendorContacts Fetch

	const onCreateVendorInfo = (data: CreateVendorInfoPayload) => {
		return createVendorInfoMutateAsync(data, {
			onError: (error) => {
				console.log(error)
			},
		})
	}

	const onCreateVendorContacts = (data: CreateVendorContactsPayload) => {
		return createVendorContactsMutateAsync(data, {
			onError: (error) => {
				console.log(error)
			},
		})
	}

	const onSubmit: SubmitHandler<SignupFormType> = async (data: SignupFormType) => {
		const {
			username,
			password,
			name,
			brandLogo,
			vendorStoreLink,
			companyDescription,
			ceoName,
			phone,
			email,
			address,
			salesReport,
			salesReportNumber,
			businessRegistration,
			businessRegistrationNumber,
			companyName,
			vendorContacts,
		} = data

		const signupRes = await signupMutateAsync(
			{
				username,
				password,
			},
			{
				onError: (err) => {
					const errData = err.response?.data.error
					if (errData?.detail_code === 'USERNAME_ALREADY_EXIST') {
						methods.setError(
							'username',
							{ type: 'custom', message: '이미 존재하는 아이디입니다.' },
							{ shouldFocus: true },
						)
					}
				},
			},
		)

		const { no, username: vendorname, role_no, role } = signupRes.data.inserted_item

		const createVendorInfoPayload = {
			name,
			profile_img: brandLogo,
			vendor_store_link: vendorStoreLink,
			company_description: companyDescription,
			ceo_name: ceoName,
			phone,
			email,
			address,
			sales_report: salesReport,
			sales_report_number: salesReportNumber,
			business_registration: businessRegistration,
			business_registration_number: businessRegistrationNumber,
			company_name: companyName,
		}

		for (const contactData of vendorContacts) {
			contactData.vendorNo = no
		}

		const [createVendorInfo] = await Promise.all([
			onCreateVendorInfo(createVendorInfoPayload),
			onCreateVendorContacts(vendorContacts),
		])

		const {
			profile_img: profileImg,
			company_description,
			name: brandName,
		} = createVendorInfo.data.inserted_item

		setVendor((vendorData) => ({
			...vendorData,
			isLoggedIn: true,
			no,
			username: vendorname,
			profileImg,
			description: company_description,
			brandName,
			role: role.role,
		}))

		const analytics = getAnalytics()
		setUserId(analytics, no)
		setUserProperties(analytics, {
			username: vendorname,
		})

		navigate('/', { replace: true })
	}

	const onError = (errors: FieldErrors) => {
		console.log(errors)
	}

	return {
		methods,
		handleSubmit: methods.handleSubmit(onSubmit, onError),
		isLoading,
	}
}

export default useSignup
